import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import {
  getCurrentGameweek,
  getPredictionsForGameweek,
  savePrediction,
} from "../services/predictionService";
import { getLeagueById, copyPredictionsToOtherLeagues } from "../services/leagueService";
import { getErrorMessage } from "../services/api";
import FixtureCard from "../components/FixtureCard";
import AlertMessage from "../components/AlertMessage";
import PageHeader from "../components/PageHeader";
import SectionCard from "../components/SectionCard";
import EmptyState from "../components/EmptyState";
import StatPill from "../components/StatPill";
import BackButton from "../components/BackButton";
import { fireConfetti } from "../utils/confetti";

function isValidScoreInput(value) {
  return value === "" || /^[0-9]+$/.test(value);
}

function parseScore(value) {
  if (value === "") return null;
  const num = Number(value);
  return Number.isInteger(num) && num >= 0 ? num : null;
}

function getGameweekLabel(gameweek, tournamentType) {
  if (!gameweek) return "—";
  
  if (tournamentType === "WorldCup") {
    // For World Cup, use the gameweek name directly (e.g., "Group Stage - Matchday 1", "Round of 16")
    return gameweek.name || `Round ${gameweek.number}`;
  }
  
  // For Premier League, show traditional format
  return gameweek.name || `Gameweek ${gameweek.number}`;
}

function GameweekPicksPage({ userId }) {
  const { leagueId } = useParams();

  const numericLeagueId = Number(leagueId);

  const [league, setLeague] = useState(null);
  const [gameweek, setGameweek] = useState(null);
  const [fixtures, setFixtures] = useState([]);
  const [scores, setScores] = useState({});
  const [loading, setLoading] = useState(true);
  const [pageMessage, setPageMessage] = useState("");
  const [savingFixtureId, setSavingFixtureId] = useState(null);
  const [savingAll, setSavingAll] = useState(false);
  const [fixtureStatuses, setFixtureStatuses] = useState({});

  const timeoutRefs = useRef({});
  const [isCopying, setIsCopying] = useState(false);
  const [showCopyConfirm, setShowCopyConfirm] = useState(false);

  useEffect(() => {
    if (!userId || !numericLeagueId) {
      setLoading(false);
      return;
    }

    if (!Number.isInteger(numericLeagueId) || numericLeagueId <= 0) {
      setLoading(false);
      setPageMessage("Invalid league.");
      return;
    }

    const controller = new AbortController();

    async function load() {
      setLoading(true);
      setPageMessage("");

      try {
        const [leagueData, currentGameweek] = await Promise.all([
          getLeagueById(numericLeagueId, { signal: controller.signal }),
          getCurrentGameweek(numericLeagueId, { signal: controller.signal }),
        ]);

        setLeague(leagueData);
        setGameweek(currentGameweek);

        const predictionView = await getPredictionsForGameweek(
          userId,
          numericLeagueId,
          currentGameweek.id,
          { signal: controller.signal }
        );

        setFixtures(predictionView);

        const initialScores = {};
        predictionView.forEach((fixture) => {
          initialScores[fixture.fixtureId] = {
            home:
              fixture.predictedHomeScore != null
                ? String(fixture.predictedHomeScore)
                : "",
            away:
              fixture.predictedAwayScore != null
                ? String(fixture.predictedAwayScore)
                : "",
          };
        });

        setScores(initialScores);
      } catch (error) {
        if (error?.name === "AbortError") return;
        console.error(error);
        setPageMessage(getErrorMessage(error));
      } finally {
        setLoading(false);
      }
    }

    load();

    return () => {
      controller.abort();
      // eslint-disable-next-line react-hooks/exhaustive-deps
      const timeouts = timeoutRefs.current;
      Object.values(timeouts).forEach(clearTimeout);
    };
  }, [userId, numericLeagueId]);

  const gameweekLockInfo = useMemo(() => {
    if (fixtures.length > 0) {
      return {
        isLocked: Boolean(fixtures[0].isLocked),
        lockUtc: fixtures[0].lockUtc || gameweek?.lockUtc || null,
      };
    }

    return {
      isLocked: Boolean(gameweek?.isLocked),
      lockUtc: gameweek?.lockUtc || null,
    };
  }, [fixtures, gameweek]);

  function updateScore(fixtureId, side, value) {
    if (gameweekLockInfo.isLocked) return;
    if (!isValidScoreInput(value)) return;

    setScores((current) => ({
      ...current,
      [fixtureId]: {
        ...current[fixtureId],
        [side]: value,
      },
    }));

    setFixtureStatuses((current) => ({
      ...current,
      [fixtureId]: "",
    }));
  }

  async function handleSaveAll() {
    if (gameweekLockInfo.isLocked || savingAll) return;

    setSavingAll(true);
    setPageMessage("");

    const toSave = fixtures.filter((f) => {
      const score = scores[f.fixtureId];
      return parseScore(score?.home) !== null && parseScore(score?.away) !== null;
    });

    if (toSave.length === 0) {
      setPageMessage("No valid scores to save. Enter scores for all fixtures first.");
      setSavingAll(false);
      return;
    }

    let successCount = 0;
    const newStatuses = {};

    await Promise.allSettled(
      toSave.map(async (fixture) => {
        const score = scores[fixture.fixtureId];
        try {
          await savePrediction({
            userId,
            leagueId: numericLeagueId,
            fixtureId: fixture.fixtureId,
            predictedHomeScore: parseScore(score.home),
            predictedAwayScore: parseScore(score.away),
          });
          newStatuses[fixture.fixtureId] = "Saved";
          successCount++;
        } catch (error) {
          newStatuses[fixture.fixtureId] = getErrorMessage(error);
        }
      })
    );

    setFixtureStatuses((current) => ({ ...current, ...newStatuses }));

    // Update fixtures state to reflect newly saved predictions
    setFixtures((currentFixtures) =>
      currentFixtures.map((f) => {
        if (newStatuses[f.fixtureId] === "Saved") {
          return {
            ...f,
            predictedHomeScore: parseScore(scores[f.fixtureId].home),
            predictedAwayScore: parseScore(scores[f.fixtureId].away),
          };
        }
        return f;
      })
    );
    setPageMessage(`${successCount} of ${toSave.length} picks saved.`);
    if (successCount > 0) {
      fireConfetti();
    }
    setSavingAll(false);
  }

  async function handleSavePrediction(fixtureId) {
    if (gameweekLockInfo.isLocked) {
      setPageMessage("This gameweek is locked.");
      return;
    }

    const score = scores[fixtureId];
    const home = parseScore(score?.home);
    const away = parseScore(score?.away);

    if (home === null || away === null) {
      setFixtureStatuses((current) => ({
        ...current,
        [fixtureId]: "Enter valid scores (0 or more)",
      }));
      return;
    }

    try {
      setSavingFixtureId(fixtureId);
      setPageMessage("");

      await savePrediction({
        userId,
        leagueId: numericLeagueId,
        fixtureId,
        predictedHomeScore: home,
        predictedAwayScore: away,
      });

      setFixtureStatuses((current) => ({
        ...current,
        [fixtureId]: "Saved",
      }));

      if (timeoutRefs.current[fixtureId]) {
        clearTimeout(timeoutRefs.current[fixtureId]);
      }

      timeoutRefs.current[fixtureId] = setTimeout(() => {
        setFixtureStatuses((current) => ({
          ...current,
          [fixtureId]: "",
        }));
      }, 2000);
    } catch (error) {
      console.error(error);

      setFixtureStatuses((current) => ({
        ...current,
        [fixtureId]: getErrorMessage(error),
      }));
    } finally {
      setSavingFixtureId(null);
    }
  }

  async function handleCopyToOtherLeagues() {
    if (isCopying) return;

    setIsCopying(true);
    setPageMessage("");

    try {
      const result = await copyPredictionsToOtherLeagues(
        userId,
        numericLeagueId,
        gameweek.id
      );

      setPageMessage(`✅ Picks copied to ${result.copiedCount} other league${result.copiedCount !== 1 ? 's' : ''}.`);
      setShowCopyConfirm(false);
    } catch (error) {
      console.error(error);
      setPageMessage(getErrorMessage(error));
    } finally {
      setIsCopying(false);
    }
  }

  const savedCount = fixtures.filter(
    (f) => f.predictedHomeScore != null && f.predictedAwayScore != null
  ).length;

  const gameweekLabel = getGameweekLabel(gameweek, league?.tournamentType);

  if (!userId) {
    return (
      <SectionCard>
        <p className="text-slate-600">Loading user...</p>
      </SectionCard>
    );
  }

  if (!Number.isInteger(numericLeagueId) || numericLeagueId <= 0) {
    return (
      <SectionCard>
        <p className="text-slate-600">Invalid league.</p>
      </SectionCard>
    );
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-9 w-52 animate-pulse rounded-full bg-slate-200" />
        <div className="rounded-3xl bg-white p-4 sm:p-6 shadow-sm ring-1 ring-slate-200">
          <div className="h-8 w-56 animate-pulse rounded bg-slate-200" />
          <div className="mt-2 h-4 w-32 animate-pulse rounded bg-slate-200" />
        </div>
        <div className="grid gap-4 sm:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="rounded-2xl bg-slate-50 px-4 py-4 ring-1 ring-slate-200">
              <div className="h-3 w-16 animate-pulse rounded bg-slate-200" />
              <div className="mt-2 h-6 w-24 animate-pulse rounded bg-slate-200" />
            </div>
          ))}
        </div>
        {[1, 2, 3].map((i) => (
          <div key={i} className="rounded-3xl bg-white p-4 sm:p-5 shadow-sm ring-1 ring-slate-200">
            <div className="mb-4 flex justify-between">
              <div className="h-4 w-24 animate-pulse rounded bg-slate-200" />
              <div className="h-6 w-16 animate-pulse rounded-full bg-slate-200" />
            </div>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 py-2">
              <div className="h-5 w-28 animate-pulse rounded bg-slate-200" />
              <div className="h-12 sm:h-14 w-12 sm:w-14 animate-pulse rounded-2xl bg-slate-100" />
              <div className="h-12 sm:h-14 w-12 sm:w-14 animate-pulse rounded-2xl bg-slate-100" />
              <div className="h-5 w-28 animate-pulse rounded bg-slate-200" />
            </div>
          </div>
        ))}
      </div>
    );
  }



  return (
    <div className="space-y-6">
      <BackButton to={`/leagues/${leagueId}`} label="Back to League Overview" />

      <PageHeader
        title={league?.name || "Gameweek Picks"}
        subtitle={gameweekLabel}
      />

      <AlertMessage message={pageMessage} type="error" />

      {fixtures.length > 0 && (
        <section className="grid gap-4 sm:grid-cols-3">
          <StatPill label="Fixtures" value={fixtures.length} />
          <StatPill label="Saved Picks" value={savedCount} />
          <StatPill label="Status" value={gameweekLockInfo.isLocked ? "Locked" : "Open"} />
        </section>
      )}

      {!gameweekLockInfo.isLocked && fixtures.length > 0 && (
        <div className="flex gap-3 justify-end">
          <button
            onClick={() => setShowCopyConfirm(true)}
            disabled={isCopying || savingAll || !!savingFixtureId}
            className="inline-flex items-center gap-2 rounded-2xl bg-blue-600 px-3 sm:px-5 py-2 sm:py-3 text-xs sm:text-sm font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isCopying ? (
              <>
                <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 100 16v-4l-3 3 3 3v-4a8 8 0 01-8-8z" />
                </svg>
                Copying...
              </>
            ) : (
              "📋 Copy to Other Leagues"
            )}
          </button>

          <button
            onClick={handleSaveAll}
            disabled={savingAll || !!savingFixtureId || isCopying}
            className="inline-flex items-center gap-2 rounded-2xl bg-emerald-600 px-3 sm:px-5 py-2 sm:py-3 text-xs sm:text-sm font-semibold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {savingAll ? (
              <>
                <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 100 16v-4l-3 3 3 3v-4a8 8 0 01-8-8z" />
                </svg>
                Saving...
              </>
            ) : (
              "Save All Picks"
            )}
          </button>
        </div>
      )}

      {showCopyConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl p-6 max-w-sm w-full shadow-lg">
            <h3 className="text-lg font-semibold text-slate-900 mb-2">
              Copy Picks to Other Leagues?
            </h3>
            <p className="text-sm text-slate-600 mb-4">
              Your <strong>saved picks</strong> will be copied to all your other {league?.tournamentType === "WorldCup" ? "World Cup" : "Premier League"} leagues for this gameweek. Unsaved picks will not be copied.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowCopyConfirm(false)}
                disabled={isCopying}
                className="px-4 py-2 rounded-2xl border border-slate-300 bg-white text-slate-800 font-semibold text-sm transition hover:bg-slate-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleCopyToOtherLeagues}
                disabled={isCopying}
                className="px-4 py-2 rounded-2xl bg-blue-600 text-white font-semibold text-sm transition hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isCopying ? "Copying..." : "Copy Picks"}
              </button>
            </div>
          </div>
        </div>
      )}

      {fixtures.length === 0 ? (
        <EmptyState title="No fixtures found" />
      ) : (
        fixtures.map((fixture) => (
          <FixtureCard
            key={fixture.fixtureId}
            fixture={fixture}
            score={scores[fixture.fixtureId]}
            locked={gameweekLockInfo.isLocked}
            isSaving={savingFixtureId === fixture.fixtureId}
            statusMessage={fixtureStatuses[fixture.fixtureId]}
            onScoreChange={(side, value) => updateScore(fixture.fixtureId, side, value)}
            onSave={() => handleSavePrediction(fixture.fixtureId)}
          />
        ))
      )}
    </div>
  );
}

export default GameweekPicksPage;
